// src/components/admin/appointments/AppointmentFilters.jsx
import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Search,
  X,
  ChevronDown,
  ChevronUp,
  Calendar,
  Users,
  Briefcase,
  CreditCard,
  SortAsc,
  SortDesc,
  Trash2,
  Filter,
  Loader2,
  AlertCircle,
} from "lucide-react";
import moment from "moment-jalaali";
import useAxiosAdmin from "../../lib/axiosAdmin";
import {
  setAllDataOfAdminAppointments,
  setFilteredAdminAppointments,
} from "../../../../redux/action/admin/appointments/action";
import {
  setAllDataOfAdminStaff,
} from "../../../../redux/action/admin/staff/action";
import {
  setAllDataOfAdminServices,
} from "../../../../redux/action/admin/services/action";
import { SET_HAS_MORE_PAGE_APPOINTMENTS } from "../../../../redux/action/admin/appointments/type";

const STORAGE_KEY = "admin_appointments_filters";
const ACCORDION_KEY = "admin_appointments_accordion_state";
const TIME_SLOTS = [
  "07:00","07:30","08:00","08:30","09:00","09:30","10:00","10:30",
  "11:00","11:30","12:00","12:30","13:00","13:30","14:00","14:30",
  "15:00","15:30","16:00","16:30","17:00","17:30","18:00","18:30",
  "19:00","19:30","20:00","20:30","21:00","21:30","22:00",
];

const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

const toMinutes = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw)
    .trim()
    .replace(/[۰-۹]/g, (d) => String(PERSIAN_DIGITS.indexOf(d)));
  const match = s.match(/(\d{1,2})\s*[:.]\s*(\d{1,2})/);
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
};

const applyHourFilterAndSort = (list, timeFrom, timeTo, sortOrder = "asc") => {
  const fromM = toMinutes(timeFrom);
  const toM = toMinutes(timeTo);
  let next = Array.isArray(list) ? [...list] : [];

  if (fromM != null || toM != null) {
    next = next.filter((a) => {
      const mins = toMinutes(a?.datetime?.time);
      if (mins == null) return false;
      if (fromM != null && mins < fromM) return false;
      if (toM != null && mins > toM) return false;
      return true;
    });
  }

  const orderMul = sortOrder === "desc" ? -1 : 1;
  next.sort((a, b) => {
    const timeDiff = (toMinutes(a?.datetime?.time) ?? 0) - (toMinutes(b?.datetime?.time) ?? 0);
    if (timeDiff !== 0) return timeDiff * orderMul;
    return (a?.datetime?.date || "").localeCompare(b?.datetime?.date || "") * orderMul;
  });

  return next;
};


const AppointmentFilters = ({loadingMore, setLoadingMore, hasMore, setHasMore}) => {
  const dispatch = useDispatch();
  const axiosAdmin = useAxiosAdmin();

  // Redux states
  const { staff: staffList, loaded: staffLoaded } = useSelector(
    (state) => state.adminStaff
  );
  const { services: servicesList, loaded: servicesLoaded } = useSelector(
    (state) => state.adminServices
  );
    const { appointments: currentAppointments } = useSelector(
    (state) => state.adminAppointments
  );

  // Local states
  // Local states - با خواندن از localStorage
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).selectedStaff || "" : "";
  });
  const [selectedService, setSelectedService] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).selectedService || "" : "";
  });
  const [selectedPaymentType, setSelectedPaymentType] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).selectedPaymentType || "" : "";
  });
  const [sortBy, setSortBy] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).sortBy || "datetime" : "datetime";
  });
  const [sortOrder, setSortOrder] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).sortOrder || "asc" : "asc";
  });
  const [timeFilter, setTimeFilter] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).timeFilter || "" : "";
  });
  const [customStartDate, setCustomStartDate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).customStartDate || "" : "";
  });
  const [customEndDate, setCustomEndDate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).customEndDate || "" : "";
  });
  const [selectedStatus, setSelectedStatus] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).selectedStatus || "" : "";
  });

  // ── فیلترهای مستقل تاریخ و ساعت رزرو ─────────────────────────────────────
  const [dateFrom, setDateFrom] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).dateFrom || "" : "";
  });
  const [dateTo, setDateTo] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).dateTo || "" : "";
  });
  const [timeFrom, setTimeFrom] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).timeFrom || "" : "";
  });
  const [timeTo, setTimeTo] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).timeTo || "" : "";
  });

  // UI states
  const [loading, setLoading] = useState(false);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(() => {
  const saved = localStorage.getItem(ACCORDION_KEY);
  return saved ? JSON.parse(saved) : false;
});
  const [hasUnappliedChanges, setHasUnappliedChanges] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Custom date picker states
  const [startYear, setStartYear] = useState(moment().jYear());
  const [startMonth, setStartMonth] = useState(moment().jMonth() + 1);
  const [startDay, setStartDay] = useState(moment().jDate());
  const [endYear, setEndYear] = useState(moment().jYear());
  const [endMonth, setEndMonth] = useState(moment().jMonth() + 1);
  const [endDay, setEndDay] = useState(moment().jDate());

  // Specific date and time states for "specific" filter option
  const [specificDate, setSpecificDate] = useState("");
  const [specificTime, setSpecificTime] = useState("");
  const [specificYear, setSpecificYear] = useState(moment().jYear());
  const [specificMonth, setSpecificMonth] = useState(moment().jMonth() + 1);
  const [specificDay, setSpecificDay] = useState(moment().jDate());

  // تبدیل اعداد فارسی به انگلیسی
  const convertPersianToEnglish = (str) => {
    const persianNumbers = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
    const englishNumbers = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

    let result = str;
    for (let i = 0; i < 10; i++) {
      result = result.replace(new RegExp(persianNumbers[i], "g"), englishNumbers[i]);
    }
    return result;
  };

  // تبدیل اعداد انگلیسی به فارسی
  const toPersianNumber = (num) => {
    if (!num) return "";
    return num.toString().replace(/[0-9]/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);
  };

  // فقط فارسی و اعداد
  const handleCustomerSearchChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[a-zA-Z]/g, "");
    value = convertPersianToEnglish(value);
    setCustomerSearch(value);
    setHasUnappliedChanges(true);
  };

//   // بارگذاری فیلترها از localStorage
//   useEffect(() => {
//     const saved = localStorage.getItem(STORAGE_KEY);
//     if (saved) {
//       try {
//         const parsed = JSON.parse(saved);
//         setSelectedStaff(parsed.selectedStaff || "");
//         setSelectedService(parsed.selectedService || "");
//         setSelectedPaymentType(parsed.selectedPaymentType || "");
//         setSortBy(parsed.sortBy || "createdAt");
//         setSortOrder(parsed.sortOrder || "desc");
//         setTimeFilter(parsed.timeFilter || "");
//         setCustomStartDate(parsed.customStartDate || "");
//         setCustomEndDate(parsed.customEndDate || "");
//       } catch (err) {
//         console.error("خطا در بارگذاری فیلترها:", err);
//       }
//     }
//   }, []);

  // ذخیره فیلترها در localStorage
  useEffect(() => {
    const filters = {
      selectedStaff,
      selectedService,
      selectedPaymentType,
      sortBy,
      sortOrder,
      timeFilter,
      customStartDate,
      customEndDate,
      selectedStatus,
      dateFrom,
      dateTo,
      timeFrom,
      timeTo,
      specificDate,
      specificTime,
      specificYear,
      specificMonth,
      specificDay,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [
    selectedStaff,
    selectedService,
    selectedPaymentType,
    sortBy,
    sortOrder,
    timeFilter,
    customStartDate,
    customEndDate,
    selectedStatus,
    dateFrom,
    dateTo,
    timeFrom,
    timeTo,
    specificDate,
    specificTime,
    specificYear,
    specificMonth,
    specificDay,
  ]);

      // ذخیره وضعیت آکاردیون
  useEffect(() => {
    localStorage.setItem(ACCORDION_KEY, JSON.stringify(isAccordionOpen));
  }, [isAccordionOpen]);


  useEffect(() => {

    // فقط دسکتاپ
    if (window.innerWidth >= 1024) {

        window.scrollTo({
            left: -10000,
            behavior: "smooth"
        });

    }

}, []);


  // دریافت کارکنان
  const fetchStaff = useCallback(async () => {
    if (staffLoaded && staffList.length > 0) return;

    try {
      setLoadingStaff(true);
      const res = await axiosAdmin.get("/api/admin/staff", {
        params: { limit: 500 },
      });

      dispatch(
        setAllDataOfAdminStaff({
          page: res.data.page || 0,
          total: res.data.total || 0,
          pages: res.data.pages || 0,
          staff: res.data.staff || [],
        })
      );
    } catch (err) {
      console.error("خطا در دریافت کارکنان:", err);
    } finally {
      setLoadingStaff(false);
    }
  }, [axiosAdmin, dispatch, staffLoaded, staffList.length]);

  // دریافت خدمات
  const fetchServices = useCallback(async () => {
    if (servicesLoaded && servicesList.length > 0) return;

    try {
      setLoadingServices(true);
      const res = await axiosAdmin.get("/api/admin/services", {
        params: { limit: 500 },
      });

      dispatch(
        setAllDataOfAdminServices({
          page: res.data.page || 0,
          total: res.data.total || 0,
          pages: res.data.pages || 0,
          services: res.data.services || [],
        })
      );
    } catch (err) {
      console.error("خطا در دریافت خدمات:", err);
    } finally {
      setLoadingServices(false);
    }
  }, [axiosAdmin, dispatch, servicesLoaded, servicesList.length]);

  // دریافت قرارها با فیلتر
    // دریافت قرارها با فیلتر و صفحه‌بندی
   // دریافت قرارها با فیلتر و صفحه‌بندی
  const fetchAppointments = useCallback(async (page = 1, append = false, overrides = {}) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const nextTimeFilter = overrides.timeFilter ?? timeFilter;
      const nextCustomStart = overrides.customStartDate ?? customStartDate;
      const nextCustomEnd = overrides.customEndDate ?? customEndDate;
      const nextTimeFrom = overrides.timeFrom ?? timeFrom;
      const nextTimeTo = overrides.timeTo ?? timeTo;
      const nextSpecificTime = overrides.specificTime ?? specificTime;
      const nextSortBy = overrides.sortBy ?? sortBy;

      const params = {
        page,
        limit: 10000,
      };

      if (customerSearch.trim()) {
        params.customerSearch = customerSearch.trim();
      }

      if (selectedStaff) {
        params.staffId = selectedStaff;
      }

      if (selectedService) {
        params.serviceId = selectedService;
      }

      if (selectedPaymentType) {
        params.paymentType = selectedPaymentType;
      }

      if (selectedStatus) {
        params.status = selectedStatus;
      }

      // ── فیلترهای مستقل تاریخ و ساعت رزرو ──────────────────────────────────
      if (nextTimeFilter !== "custom") {
        if (dateFrom)  params.dateFrom  = dateFrom;
        if (dateTo)    params.dateTo    = dateTo;
      }
      // فیلتر ساعت روی کلاینت اعمال می‌شود تا با فرمت‌های مختلف ساعت رزرو درست کار کند

      if (nextTimeFilter) {
        params.timeFilter = nextTimeFilter;
        if (nextTimeFilter === "custom" && nextCustomStart && nextCustomEnd) {
          params.customStartDate = nextCustomStart;
          params.customEndDate = nextCustomEnd;
        }
        if (nextTimeFilter === "specific") {
          if (specificDate) {
            params.specificDate = specificDate;
          }
          if (nextSpecificTime) {
            params.specificTime = nextSpecificTime;
          }
        }
      }

      const hourFilterActive = Boolean(nextTimeFrom || nextTimeTo);
      params.sortBy = hourFilterActive ? "time" : nextSortBy;
      params.sortOrder = hourFilterActive ? "asc" : sortOrder;

      const response = await axiosAdmin.get("/api/admin/appointment", {
        params,
      });

      let appointmentsData = response.data.appointments || [];
      if (hourFilterActive) {
        appointmentsData = applyHourFilterAndSort(
          appointmentsData,
          nextTimeFrom,
          nextTimeTo,
          "asc"
        );
      }
      const total = hourFilterActive ? appointmentsData.length : (response.data.total || 0);
      const pages = hourFilterActive ? 1 : (response.data.pages || 0);

      console.log("📥 Response:", { total, count: appointmentsData.length, firstItem: appointmentsData[0]?.datetime });

      if (append) {
        // اضافه کردن به لیست موجود
        const mergedAppointments = [...currentAppointments, ...appointmentsData];
        dispatch(
          setAllDataOfAdminAppointments({
            appointments: mergedAppointments,
            total,
            page,
            pages,
          })
        );
        dispatch(setFilteredAdminAppointments(mergedAppointments));
      } else {
        // جایگزینی کامل
        dispatch(
          setAllDataOfAdminAppointments({
            appointments: appointmentsData,
            total,
            page,
            pages,
          })
        );
        dispatch(setFilteredAdminAppointments(appointmentsData));
      }

      setCurrentPage(page);
      setHasMore(page < pages);
      dispatch({
        type: SET_HAS_MORE_PAGE_APPOINTMENTS,
        payload: page < pages
      })
      setHasUnappliedChanges(false);
    
    } catch (error) {
      console.error("خطا در دریافت قرارها:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [
    axiosAdmin,
    dispatch,
    customerSearch,
    selectedStaff,
    selectedStatus,
    selectedService,
    selectedPaymentType,
    sortBy,
    sortOrder,
    timeFilter,
    customStartDate,
    customEndDate,
    dateFrom,
    dateTo,
    timeFrom,
    timeTo,
    specificTime,
    specificDate,
    currentAppointments,
    setHasMore,
    setLoadingMore,
  ]);

  // بارگذاری اولیه با فیلترهای ذخیره شده
  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // dependency array خالی بمونه

  // حذف همه فیلترها
   const clearAllFilters = () => {
    setCustomerSearch("");
    setSelectedStaff("");
    setSelectedService("");
    setSelectedStatus("");
    setSelectedPaymentType("");
    setSortBy("datetime");
    setSortOrder("asc");
    setTimeFilter("");
    setCustomStartDate("");
    setCustomEndDate("");
    setDateFrom(""); setDateTo("");
    setTimeFrom(""); setTimeTo("");
    setSpecificDate(""); setSpecificTime("");
    setSpecificYear(moment().jYear());
    setSpecificMonth(moment().jMonth() + 1);
    setSpecificDay(moment().jDate());
    localStorage.removeItem(STORAGE_KEY);
    setHasUnappliedChanges(true);
    setCurrentPage(1);
    setHasMore(false);
  };


  // حذف فیلتر خاص
  const removeFilter = (filterName) => {
    switch (filterName) {
      case "customer":
        setCustomerSearch("");
        break;
      case "staff":
        setSelectedStaff("");
        break;
      case "service":
        setSelectedService("");
        break;
      case "payment":
        setSelectedPaymentType("");
        break;
    case "status":
        setSelectedStatus("");
        break;
      case "time":
        setTimeFilter("");
        setCustomStartDate("");
        setCustomEndDate("");
        setSpecificDate("");
        setSpecificTime("");
        setSpecificYear(moment().jYear());
        setSpecificMonth(moment().jMonth() + 1);
        setSpecificDay(moment().jDate());
        break;
      case "daterange":
        setDateFrom(""); setDateTo("");
        break;
      case "timerange":
        setTimeFrom(""); setTimeTo("");
        break;
      default:
        break;
    }
    setHasUnappliedChanges(true);
  };

  // انتخاب زمان از لیست
  const handleTimeFilterSelect = (filter) => {
    console.log("=== handleTimeFilterSelect called ===");
    console.log("Selected filter:", filter);
    setTimeFilter(filter);
    setHasUnappliedChanges(true);
    
    if (filter === "custom") {
      setShowCustomDatePicker(true);
    } else {
      setShowTimeModal(false);
      setCustomStartDate("");
      setCustomEndDate("");
      setTimeFrom("");
      setTimeTo("");
    }
  };

  // تایید تاریخ/ساعت سفارشی
  const handleCustomDateConfirm = () => {
    const start = moment(
      `${startYear}/${startMonth}/${startDay}`,
      "jYYYY/jM/jD"
    ).format("YYYY-MM-DD");
    const end = moment(
      `${endYear}/${endMonth}/${endDay}`,
      "jYYYY/jM/jD"
    ).format("YYYY-MM-DD");

    const startDate = start <= end ? start : end;
    const endDate = start <= end ? end : start;
    const hourSort = Boolean(timeFrom || timeTo);
    if (hourSort) {
      setSortBy("time");
      setSortOrder("asc");
    }
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
    setTimeFilter("custom");
    setShowCustomDatePicker(false);
    setShowTimeModal(false);

    fetchAppointments(1, false, {
      timeFilter: "custom",
      customStartDate: startDate,
      customEndDate: endDate,
      timeFrom,
      timeTo,
      sortBy: hourSort ? "time" : sortBy,
    });
  };

  // تولید روزهای ماه
  const getDaysInMonth = (year, month) => {
    return moment.jDaysInMonth(year, month - 1);
  };

  // لیست سال‌ها
  const years = [];
  const currentYear = moment().jYear();
  for (let i = currentYear - 2; i <= currentYear + 2; i++) {
    years.push(i);
  }

  // لیست ماه‌ها
  const months = [
    { value: 1, label: "فروردین" },
    { value: 2, label: "اردیبهشت" },
    { value: 3, label: "خرداد" },
    { value: 4, label: "تیر" },
    { value: 5, label: "مرداد" },
    { value: 6, label: "شهریور" },
    { value: 7, label: "مهر" },
    { value: 8, label: "آبان" },
    { value: 9, label: "آذر" },
    { value: 10, label: "دی" },
    { value: 11, label: "بهمن" },
    { value: 12, label: "اسفند" },
  ];

  // گزینه‌های زمان
  const timeOptions = [
    { value: "", label: "همه زمان‌ها" },
    { value: "today", label: "امروز" },
    { value: "yesterday", label: "دیروز" },
    { value: "tomorrow", label: "فردا" },
    { value: "thisWeek", label: "این هفته" },
    { value: "thisMonth", label: "این ماه" },
    { value: "next3Days", label: "تا ۳ روز آینده" },
    { value: "next7Days", label: "تا ۷ روز آینده" },
    { value: "next30Days", label: "تا ۳۰ روز آینده" },
    { value: "custom", label: "سفارشی" },
  ];

  // گزینه‌های نوع پرداخت
  const paymentTypes = [
    { value: "", label: "همه روش‌ها" },
    { value: "درگاه پرداخت", label: "درگاه پرداخت" },
    { value: "کارت به کارت", label: "کارت به کارت" },
    { value: "دستگاه POS", label: "دستگاه POS" },
    { value: "نامعلوم", label: "نامعلوم" },
  ];

    // گزینه‌های وضعیت رزرو
  const statusOptions = [
    { value: "", label: "همه وضعیت‌ها" },
    { value: "تایید شده", label: "تایید شده" },
    { value: "لغو شده", label: "لغو شده" },
    { value: "درحال پرداخت", label: "درحال پرداخت" },
  ];

  // گزینه‌های سورت
  const sortOptions = [
    { value: "createdAt", label: "زمان ثبت" },
    { value: "datetime", label: "تاریخ قرار" },
    { value: "time", label: "ساعت قرار" },
    { value: "customerName", label: "نام مشتری" },
    { value: "staffName", label: "نام کارکن" },
    { value: "serviceName", label: "نام خدمت" },
    { value: "paymentType", label: "نوع پرداخت" },
  ];

  // نمایش فیلترهای فعال
  const activeFilters = [];
  if (customerSearch) activeFilters.push({ type: "customer", label: `مشتری: ${customerSearch}` });
  if (selectedStaff) {
    const staff = staffList.find((s) => s._id === selectedStaff);
    if (staff) activeFilters.push({ type: "staff", label: `کارکن: ${staff.information?.firstName} ${staff.information?.lastName}` });
  }
  if (selectedService) {
    const service = servicesList.find((s) => s._id === selectedService);
    if (service) activeFilters.push({ type: "service", label: `خدمت: ${service.name}` });
  }
  if (selectedPaymentType) {
    activeFilters.push({ type: "payment", label: `پرداخت: ${selectedPaymentType}` });
  }
    if (selectedStatus) {
    activeFilters.push({ type: "status", label: `وضعیت: ${selectedStatus}` });
  }
  if (timeFilter) {
    const timeOption = timeOptions.find((t) => t.value === timeFilter);
    activeFilters.push({ type: "time", label: `زمان: ${timeOption?.label}` });
  }
  if (dateFrom || dateTo) {
    const fromJ = dateFrom ? moment(dateFrom,"YYYY-MM-DD").format("jYYYY/jMM/jDD") : "";
    const toJ   = dateTo   ? moment(dateTo,  "YYYY-MM-DD").format("jYYYY/jMM/jDD") : "";
    activeFilters.push({ type: "daterange", label: `تاریخ رزرو: ${fromJ}${fromJ && toJ ? " تا " : ""}${toJ}` });
  }
  if (timeFrom || timeTo) {
    activeFilters.push({ type: "timerange", label: `ساعت رزرو: ${timeFrom||""}${timeFrom && timeTo ? " تا " : ""}${timeTo||""}` });
  }

  // در انتهای کامپوننت AppointmentFilters، قبل از return اصلی
    useEffect(() => {
    // ارسال تابع loadMore به والد از طریق prop
    if (typeof onLoadMore === 'function') {
        // فقط اگر prop وجود داشت
    }
    }, [currentPage, hasMore, loadingMore]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm mb-6" dir="rtl">
      {/* Accordion Header */}
      <button
        onClick={() => setIsAccordionOpen(!isAccordionOpen)}
        className="w-full px-4 md:px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-2xl"
      >
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-purple-600" />
          <h2 className="text-lg font-bold text-gray-800">فیلتر و جستجو</h2>
          {activeFilters.length > 0 && (
            <span className="bg-purple-100 text-purple-700 text-xs font-bold px-2 py-1 rounded-full">
              {activeFilters.length}
            </span>
          )}
        </div>
        {isAccordionOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-600" />
        )}
      </button>

      {/* Accordion Content */}
      {isAccordionOpen && (
        <div className="px-4 md:px-6 pb-6 border-t border-gray-100">
          {/* Warning Alert */}
          {hasUnappliedChanges && (
            <div className="mt-4 mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-amber-800 font-medium">
                  تغییراتی در فیلترها اعمال شده است
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  حتماً باید دکمه "اعمال فیلترها" را کلیک کنید تا نتایج به‌روز شوند
                </p>
              </div>
            </div>
          )}

          {/* Header Actions */}
          <div className="flex items-center justify-end mt-4 mb-4">
            {activeFilters.length > 0 && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>حذف همه</span>
              </button>
            )}
          </div>

          {/* Search Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              جستجوی مشتری (نام یا شماره تلفن)
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={customerSearch}
                  onChange={handleCustomerSearchChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      fetchAppointments();
                    }
                  }}
                  placeholder="نام یا شماره تلفن مشتری..."
                  className="w-full pr-10 pl-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-purple-400 focus:bg-white transition-all"
                />
                {customerSearch && (
                  <button
                    onClick={() => {
                      setCustomerSearch("");
                      setHasUnappliedChanges(true);
                    }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button
                onClick={fetchAppointments}
                disabled={loading}
                className="px-4 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white text-sm rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                <span>جستجو</span>
              </button>
            </div>
          </div>

          {/* Filters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-4">
            {/* Staff Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline-block ml-1" />
                کارکن
              </label>
              <select
                value={selectedStaff}
                onChange={(e) => {
                  setSelectedStaff(e.target.value);
                  setHasUnappliedChanges(true);
                }}
                onFocus={fetchStaff}
                disabled={loadingStaff}
                className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-purple-400 focus:bg-white transition-all disabled:opacity-50 disabled:cursor-wait"
              >
                <option value="">
                  {loadingStaff ? "در حال بارگذاری..." : "همه کارکنان"}
                </option>
                {!loadingStaff &&
                  staffList.map((staff) => (
                    <option key={staff._id} value={staff._id}>
                      {staff.information?.firstName + ' ' + staff.information?.lastName || "بدون نام"}
                    </option>
                  ))}
              </select>
            </div>

            {/* Service Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Briefcase className="w-4 h-4 inline-block ml-1" />
                خدمت
              </label>
              <select
                value={selectedService}
                onChange={(e) => {
                  setSelectedService(e.target.value);
                  setHasUnappliedChanges(true);
                }}
                onFocus={fetchServices}
                disabled={loadingServices}
                className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-purple-400 focus:bg-white transition-all disabled:opacity-50 disabled:cursor-wait"
              >
                <option value="">
                  {loadingServices ? "در حال بارگذاری..." : "همه خدمات"}
                </option>
                {!loadingServices &&
                  servicesList.map((service) => (
                    <option key={service._id} value={service._id}>
                      {service.name}
                    </option>
                  ))}
              </select>
            </div>

            {/* Payment Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <CreditCard className="w-4 h-4 inline-block ml-1" />
                نوع پرداخت
              </label>
              <select
                value={selectedPaymentType}
                onChange={(e) => {
                  setSelectedPaymentType(e.target.value);
                  setHasUnappliedChanges(true);
                }}
                className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-purple-400 focus:bg-white transition-all"
              >
                {paymentTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <AlertCircle className="w-4 h-4 inline-block ml-1" />
                وضعیت رزرو
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setHasUnappliedChanges(true);
                }}
                className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-purple-400 focus:bg-white transition-all"
              >
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Time Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline-block ml-1" />
                زمان
              </label>
              <button
                onClick={() => setShowTimeModal(true)}
                className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 hover:border-purple-400 hover:bg-white transition-all text-right flex items-center justify-between"
              >
                <span>
                  {timeFilter
                    ? timeOptions.find((t) => t.value === timeFilter)?.label
                    : "همه زمان‌ها"}
                </span>
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Sort Section - با جداسازی بصری */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sort By */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  مرتب‌سازی بر اساس
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setHasUnappliedChanges(true);
                  }}
                  className="w-full px-3 py-2.5 text-sm bg-white border border-purple-200 rounded-lg text-gray-700 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-200 transition-all"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ترتیب نمایش
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSortOrder("desc");
                      setHasUnappliedChanges(true);
                    }}
                    className={`flex-1 px-3 py-2.5 text-sm rounded-lg transition-all flex items-center justify-center gap-2 ${
                      sortOrder === "desc"
                        ? "bg-purple-600 text-white shadow-md"
                        : "bg-white text-gray-700 border border-purple-200 hover:border-purple-400"
                    }`}
                  >
                    <SortDesc className="w-4 h-4" />
                    <span>نزولی</span>
                  </button>
                  <button
                    onClick={() => {
                      setSortOrder("asc");
                      setHasUnappliedChanges(true);
                    }}
                    className={`flex-1 px-3 py-2.5 text-sm rounded-lg transition-all flex items-center justify-center gap-2 ${
                      sortOrder === "asc"
                        ? "bg-purple-600 text-white shadow-md"
                        : "bg-white text-gray-700 border border-purple-200 hover:border-purple-400"
                    }`}
                  >
                    <SortAsc className="w-4 h-4" />
                    <span>صعودی</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Active Filters */}
          {activeFilters.length > 0 && (
            <div className="mb-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">فیلترهای فعال:</p>
              <div className="flex flex-wrap gap-2">
                {activeFilters.map((filter, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 text-xs rounded-lg"
                  >
                    <span>{filter.label}</span>
                    <button
                      onClick={() => removeFilter(filter.type)}
                      className="hover:text-purple-900"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Apply Button - سبز */}
          <div className="mt-4">
            <button
              onClick={() => {
                if (timeFrom || timeTo) {
                  setSortBy("time");
                }
                fetchAppointments(1, false, {
                  sortBy: (timeFrom || timeTo) ? "time" : sortBy,
                });
              }}
              disabled={loading}
              className="w-full px-4 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>در حال اعمال فیلترها...</span>
                </>
              ) : (
                <>
                  <Filter className="w-5 h-5" />
                  <span>اعمال فیلترها و نمایش نتایج</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Time Filter Modal */}
      {showTimeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-lg font-bold text-gray-800">انتخاب بازه زمانی</h3>
              <button
                onClick={() => {
                  setShowTimeModal(false);
                  setShowCustomDatePicker(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {!showCustomDatePicker ? (
              <div className="p-6 space-y-2">
                {timeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleTimeFilterSelect(option.value)}
                    className={`w-full px-4 py-3 text-sm text-right rounded-lg transition-all ${
                      timeFilter === option.value
                        ? "bg-purple-600 text-white shadow-md"
                        : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  {/* Start Date */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-700 mb-3">از تاریخ</h4>
                    
                    <label className="block text-xs text-gray-600 mb-1">سال</label>
                    <select
                      value={startYear}
                      onChange={(e) => setStartYear(Number(e.target.value))}
                      className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg mb-3 focus:outline-none focus:border-purple-400"
                    >
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>

                    <label className="block text-xs text-gray-600 mb-1">ماه</label>
                    <select
                      value={startMonth}
                      onChange={(e) => setStartMonth(Number(e.target.value))}
                      className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg mb-3 focus:outline-none focus:border-purple-400"
                    >
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>

                    <label className="block text-xs text-gray-600 mb-1">روز</label>
                    <select
                      value={startDay}
                      onChange={(e) => setStartDay(Number(e.target.value))}
                      className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                    >
                      {Array.from(
                        {
                          length: getDaysInMonth(startYear, startMonth),
                        },
                        (_, i) => i + 1
                      ).map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* End Date */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-700 mb-3">تا تاریخ</h4>

                    <label className="block text-xs text-gray-600 mb-1">سال</label>
                    <select
                      value={endYear}
                      onChange={(e) => setEndYear(Number(e.target.value))}
                      className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg mb-3 focus:outline-none focus:border-purple-400"
                    >
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>

                    <label className="block text-xs text-gray-600 mb-1">ماه</label>
                    <select
                      value={endMonth}
                      onChange={(e) => setEndMonth(Number(e.target.value))}
                      className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg mb-3 focus:outline-none focus:border-purple-400"
                    >
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>

                    <label className="block text-xs text-gray-600 mb-1">روز</label>
                    <select
                      value={endDay}
                      onChange={(e) => setEndDay(Number(e.target.value))}
                      className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                    >
                      {Array.from(
                        {
                          length: getDaysInMonth(endYear, endMonth),
                        },
                        (_, i) => i + 1
                      ).map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* فیلتر ساعت */}
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <h4 className="text-sm font-bold text-gray-700 mb-3">فیلتر بر اساس ساعت رزرو</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">از ساعت</label>
                      <select
                        value={timeFrom}
                        onChange={(e) => setTimeFrom(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                      >
                        <option value="">همه ساعت‌ها</option>
                        {TIME_SLOTS.map((t) => (
                          <option key={`from-${t}`} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">تا ساعت</label>
                      <select
                        value={timeTo}
                        onChange={(e) => setTimeTo(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                      >
                        <option value="">همه ساعت‌ها</option>
                        {TIME_SLOTS.map((t) => (
                          <option key={`to-${t}`} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {(timeFrom || timeTo) && (
                    <p className="text-xs text-purple-600 mt-2">
                      ساعت: {timeFrom || "ابتدا"} تا {timeTo || "انتها"}
                    </p>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowCustomDatePicker(false);
                    }}
                    className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                  >
                    بازگشت
                  </button>

                  <button
                    onClick={handleCustomDateConfirm}
                    className="flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    تایید بازه
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentFilters;
